import type { UseMutationResult } from '@tanstack/react-query';
import { ZfAccordion, ZfAccordionItem, ZfBox, ZfIcon, ZfStack, ZfTextButton, ZfToggle, ZfTooltip } from '@zafire/react';
import type { AxiosError } from 'axios';
import type { FunctionComponent, JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ISetting } from '../../interfaces/simulation-settings';

interface IProps {
    showButton?: boolean;
    disableButton?: boolean;
    onButtonClick?: () => void;
    showMainToggle?: boolean;
    toggled?: boolean;
    summary?: JSX.Element;
    detail: JSX.Element;
    title: string;
    onToggle?: UseMutationResult<ISetting, AxiosError, ISetting>;
    boxId?: number;
    showWarningIcon?: boolean;
    warningText?: string;
    sectionKey?: string;
    expandedKey?: string;
}

enum AccordionState {
    default = 'default',
    active = 'active',
    disabled = 'disabled',
}

export const SettingBoxBase: FunctionComponent<IProps> = ({
    showButton = false,
    disableButton,
    onButtonClick,
    showMainToggle = true,
    toggled,
    summary,
    detail,
    title,
    onToggle,
    boxId,
    showWarningIcon,
    warningText,
    sectionKey,
    expandedKey,
}) => {
    const isExpanded = !!sectionKey && sectionKey === expandedKey;
    const [accordionState, setAccordionState] = useState<AccordionState>(isExpanded ? AccordionState.active : AccordionState.default);
    const [isToggled, setIsToggled] = useState(toggled);
    const toggleJustClickedRef = useRef(false);
    const { t } = useTranslation();

    useEffect(() => {
        setIsToggled(toggled);
    }, [toggled]);

    useEffect(() => {
        setAccordionState(isExpanded ? AccordionState.active : AccordionState.default);
    }, [isExpanded, expandedKey]);

    const handleAccordionToggle = (event: CustomEvent<boolean>) => {
        if (toggleJustClickedRef.current) {
            toggleJustClickedRef.current = false;

            return;
        }
        setAccordionState(event.detail ? AccordionState.active : AccordionState.default);
    };

    const handleToggle = (event: CustomEvent<boolean>) => {
        if (showMainToggle) {
            toggleJustClickedRef.current = true;
            setIsToggled(event.detail);
            onToggle?.mutate({ id: boxId || -1, enabled: event.detail }, { onError: () => setIsToggled(!event.detail) });
        }
    };

    return (
        <ZfBox style={{ marginBottom: 'var(--padding-bottom-md)' }}>
            <ZfAccordion>
                <ZfAccordionItem onAccordionToggle={handleAccordionToggle} state={accordionState} titleText={title}>
                    {showMainToggle && <ZfToggle slot="leading" checked={!!isToggled} onToggleChange={handleToggle} />}
                    <ZfStack direction="column" spacing="medium">
                        <ZfStack direction="column" style={{ boxSizing: 'border-box' }}>
                            {detail}
                        </ZfStack>
                        {summary}
                        {showButton && (
                            <ZfStack direction="row" justify="flex-end">
                                <ZfTextButton size="medium" hierarchy="secondary" disabled={disableButton} onClick={onButtonClick}>
                                    {t('modalEditButton')}
                                </ZfTextButton>
                            </ZfStack>
                        )}
                    </ZfStack>
                    <div slot="trailing" style={showWarningIcon ? {} : { display: 'none' }}>
                        <ZfTooltip side="top" alignment="center" text={warningText ?? ''}>
                            <ZfIcon slot="anchor" name="info" size="small" />
                        </ZfTooltip>
                    </div>
                </ZfAccordionItem>
            </ZfAccordion>
        </ZfBox>
    );
};
